// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useCallback, useRef, useState } from 'react';

import { devicesAPI, DeviceImportRow } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

import { InfrastructureCsvImport } from './InfrastructureCsvImport';

const VALID_CATEGORIES = ['lf_device', 'lf_beacon', 'lf_checkin', 'lf_gateway'];
const VALID_STATUSES = ['active', 'reserve', 'retired', 'maintenance', 'deactive'];

const TEMPLATE_HEADER =
  'deveui,name,description,device_category,device_status,nwk_key,checkpoint,supplier';
const TEMPLATE_EXAMPLE =
  '<deveui>,<serial_nr>,<description>,lf_device,<active/reserve>,<nwk_key>,0,<lora.server.lifefinder.eu>';

const CELL = {
  padding: '0.4vmin 0.8vmin',
  textAlign: 'left' as const,
  borderBottom: '1px solid #eee',
};
const HEAD = { ...CELL, background: '#f5f5f5', fontWeight: 600, borderBottom: '2px solid #ddd' };

interface ParsedRow extends DeviceImportRow {
  _rowNum: number;
  _errors: string[];
}

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}

function parseCsvText(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = splitCsvLine(lines[0]).map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/^["']|["']$/g, '')
  );

  return lines.slice(1).map((line, i) => {
    const vals = splitCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (vals[idx] ?? '').trim();
    });

    const errs: string[] = [];
    const deveui = (obj.deveui || '').toLowerCase().replace(/[^0-9a-f]/g, '');
    if (!deveui || deveui.length !== 16) errs.push('deveui must be 16 hex characters');
    const name = (obj.name || '').trim();
    if (!name) errs.push('name is required');
    else if (name.length > 45) errs.push('name exceeds 45 characters');
    if (obj.device_category && !VALID_CATEGORIES.includes(obj.device_category))
      errs.push(`device_category must be one of: ${VALID_CATEGORIES.join(', ')}`);
    if (obj.device_category === 'lf_beacon' || obj.device_category === 'lf_checkin')
      errs.push('lf_beacon / lf_checkin devices must be imported from the "Beacon / Check-in" tab');
    if (obj.device_status && !VALID_STATUSES.includes(obj.device_status))
      errs.push(`device_status must be one of: ${VALID_STATUSES.join(', ')}`);
    if (obj.nwk_key && obj.nwk_key.length > 32) errs.push('nwk_key exceeds 32 characters');

    return {
      _rowNum: i + 1,
      _errors: errs,
      deveui: deveui || obj.deveui,
      name,
      description: obj.description || '',
      device_category: obj.device_category || 'lf_device',
      device_status: obj.device_status || 'active',
      nwk_key: obj.nwk_key || '',
      checkpoint: obj.checkpoint || '0',
      supplier: obj.supplier || '',
    };
  });
}

interface ImportResult {
  inserted: number;
  chirpstackSynced: boolean;
  skipped: { deveui: string; reason: string }[];
  errors: { deveui: string; reason: string }[];
}

export const DeviceCsvImport: React.FC = () => {
  const [importType, setImportType] = useState<'device' | 'infra'>('device');
  const [rawText, setRawText] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const parse = useCallback((text: string) => {
    setRawText(text);
    setRows(parseCsvText(text));
    setResult(null);
    setSubmitError(null);
  }, []);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => parse((ev.target?.result as string) || '');
      reader.readAsText(file);
      e.target.value = '';
    },
    [parse]
  );

  const validRows = rows.filter((r) => r._errors.length === 0);
  const hasErrors = rows.some((r) => r._errors.length > 0);

  const onSubmit = useCallback(async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: DeviceImportRow[] = validRows.map(
        ({ _rowNum: _r, _errors: _e, ...rest }) => rest
      );
      const res = await devicesAPI.importDevices(payload);
      if (!res.success) throw new Error(res.error || 'Import failed');
      setResult({
        inserted: res.inserted,
        chirpstackSynced: res.chirpstackSynced,
        skipped: res.skipped,
        errors: res.errors,
      });
      setRows([]);
      setRawText('');
    } catch (err) {
      logger.error('device import failed', err);
      setSubmitError((err as Error).message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }, [validRows]);

  const downloadTemplate = () => {
    const blob = new Blob([`${TEMPLATE_HEADER}\n${TEMPLATE_EXAMPLE}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'device-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vmin', padding: '1vmin' }}>
      {/* Sub-type selector */}
      <div style={{ display: 'flex', gap: '0.6vmin' }}>
        {(
          [
            { id: 'device', label: '📱 Device' },
            { id: 'infra', label: '🏗️ Beacon / Check-in' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setImportType(t.id);
              parse('');
              setRows([]);
              setResult(null);
              setSubmitError(null);
            }}
            style={{
              background: importType === t.id ? '#1976d2' : '#eee',
              color: importType === t.id ? 'white' : '#333',
              fontWeight: importType === t.id ? 700 : 400,
              border: 'none',
              borderRadius: '0.4vmin',
              padding: '0.6vmin 1.6vmin',
              cursor: 'pointer',
              fontSize: 'max(12px, 1.6vmin)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {importType === 'infra' ? (
        <InfrastructureCsvImport />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: 'max(14px, 2vmin)' }}>CSV Device Import</h3>
            <button
              onClick={downloadTemplate}
              style={{
                background: 'transparent',
                border: '1px solid #aaa',
                borderRadius: '0.4vmin',
                padding: '0.4vmin 1vmin',
                cursor: 'pointer',
                fontSize: 'max(11px, 1.5vmin)',
              }}
            >
              Download template
            </button>
          </div>

          <div style={{ display: 'flex', gap: '1vmin', flexWrap: 'wrap' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.6vmin',
                padding: '0.5vmin 1.2vmin',
                background: '#1976d2',
                color: 'white',
                borderRadius: '0.4vmin',
                cursor: 'pointer',
                fontSize: 'max(12px, 1.6vmin)',
              }}
            >
              Upload CSV
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                style={{ display: 'none' }}
              />
            </label>
            <span style={{ alignSelf: 'center', color: '#888', fontSize: 'max(11px, 1.5vmin)' }}>
              or paste below
            </span>
          </div>

          <textarea
            value={rawText}
            onChange={(e) => parse(e.target.value)}
            placeholder={`${TEMPLATE_HEADER}\n${TEMPLATE_EXAMPLE}`}
            spellCheck={false}
            style={{
              width: '100%',
              minHeight: '10vmin',
              fontFamily: 'monospace',
              fontSize: 'max(11px, 1.4vmin)',
              resize: 'vertical',
              border: '1px solid #ccc',
              borderRadius: '0.4vmin',
              padding: '0.8vmin',
              boxSizing: 'border-box',
            }}
          />

          {rows.length > 0 && (
            <>
              <div style={{ fontSize: 'max(11px, 1.5vmin)', color: '#555' }}>
                {rows.length} row{rows.length !== 1 ? 's' : ''} parsed
                {hasErrors && (
                  <span style={{ color: '#c00', marginLeft: '1vmin' }}>
                    ({rows.filter((r) => r._errors.length > 0).length} with errors — will be
                    skipped)
                  </span>
                )}
              </div>
              <div
                style={{
                  overflow: 'auto',
                  maxHeight: '40vh',
                  border: '1px solid #ddd',
                  borderRadius: '0.4vmin',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'max(11px, 1.4vmin)',
                  }}
                >
                  <thead>
                    <tr>
                      {[
                        '#',
                        'deveui',
                        'name',
                        'description',
                        'category',
                        'status',
                        'nwk_key',
                        'checkpoint',
                        'supplier',
                        '',
                      ].map((h) => (
                        <th key={h} style={HEAD}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row._rowNum}
                        style={{ background: row._errors.length ? '#fff8f8' : undefined }}
                      >
                        <td style={CELL}>{row._rowNum}</td>
                        <td style={{ ...CELL, fontFamily: 'monospace' }}>{row.deveui}</td>
                        <td style={CELL}>{row.name}</td>
                        <td style={CELL}>{row.description}</td>
                        <td style={CELL}>{row.device_category}</td>
                        <td style={CELL}>{row.device_status}</td>
                        <td style={{ ...CELL, fontFamily: 'monospace' }}>{row.nwk_key}</td>
                        <td style={CELL}>{row.checkpoint}</td>
                        <td style={CELL}>{row.supplier}</td>
                        <td style={{ ...CELL, color: '#c00', maxWidth: '20vmin' }}>
                          {row._errors.join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: '1vmin', alignItems: 'center' }}>
                <button
                  onClick={onSubmit}
                  disabled={submitting || validRows.length === 0}
                  style={{
                    padding: '0.7vmin 2vmin',
                    background: validRows.length === 0 ? '#aaa' : '#2b7a2b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.4vmin',
                    cursor: validRows.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'max(12px, 1.6vmin)',
                    fontWeight: 600,
                  }}
                >
                  {submitting
                    ? 'Importing…'
                    : `Import ${validRows.length} device${validRows.length !== 1 ? 's' : ''}`}
                </button>
                <button
                  onClick={() => {
                    parse('');
                    setRows([]);
                  }}
                  style={{
                    padding: '0.7vmin 1.5vmin',
                    background: 'transparent',
                    border: '1px solid #aaa',
                    borderRadius: '0.4vmin',
                    cursor: 'pointer',
                    fontSize: 'max(12px, 1.6vmin)',
                  }}
                >
                  Clear
                </button>
                {submitError && (
                  <span style={{ color: '#c00', fontSize: 'max(11px, 1.5vmin)' }}>
                    {submitError}
                  </span>
                )}
              </div>
            </>
          )}

          {result && (
            <div
              style={{
                background: '#f0faf0',
                border: '1px solid #4caf50',
                borderRadius: '0.5vmin',
                padding: '1.2vmin',
                fontSize: 'max(12px, 1.6vmin)',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '0.5vmin' }}>
                Import complete — {result.inserted} inserted
                {result.chirpstackSynced && (
                  <span
                    style={{
                      marginLeft: '1vmin',
                      fontWeight: 400,
                      color: '#2b7a2b',
                      fontSize: 'max(11px, 1.4vmin)',
                    }}
                  >
                    ✓ Chirpstack sync triggered
                  </span>
                )}
              </div>
              {result.skipped.length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', color: '#666' }}>
                    {result.skipped.length} skipped (already exist)
                  </summary>
                  <ul
                    style={{
                      margin: '0.3vmin 0 0 1.5vmin',
                      padding: 0,
                      fontSize: 'max(11px, 1.4vmin)',
                    }}
                  >
                    {result.skipped.map((s) => (
                      <li key={s.deveui}>
                        {s.deveui} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {result.errors.length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', color: '#c00' }}>
                    {result.errors.length} failed
                  </summary>
                  <ul
                    style={{
                      margin: '0.3vmin 0 0 1.5vmin',
                      padding: 0,
                      fontSize: 'max(11px, 1.4vmin)',
                    }}
                  >
                    {result.errors.map((e) => (
                      <li key={e.deveui}>
                        {e.deveui} — {e.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
